import type { ColumnDef } from '@tanstack/react-table';
import type { Visita } from '@condo/contracts';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { DataTable } from '@/components/common/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useVisitasIngresos, useRegistrarEgreso } from './api';

function tipoBadge(tipo: Visita['tipo']) {
  const variantMap: Record<
    Visita['tipo'],
    'neutral' | 'warning' | 'primary' | 'success' | 'danger' | 'accent'
  > = {
    familiar: 'primary',
    delivery: 'warning',
    proveedor: 'neutral',
    servicio: 'neutral',
  };
  const labels: Record<Visita['tipo'], string> = {
    familiar: 'Familiar',
    delivery: 'Delivery',
    proveedor: 'Proveedor',
    servicio: 'Servicio',
  };
  return <Badge variant={variantMap[tipo]}>{labels[tipo]}</Badge>;
}

export function IngresosActivosTab({ condoId }: { condoId: string }) {
  const { data, isLoading } = useVisitasIngresos(condoId, {
    estado: 'ingresada',
  });
  const { mutate: registrarEgreso, isPending } = useRegistrarEgreso(condoId);

  const columns: ColumnDef<Visita, unknown>[] = [
    {
      accessorKey: 'visitorNombre',
      header: 'Visitante',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.visitorNombre}</span>
      ),
    },
    { accessorKey: 'visitorDni', header: 'DNI' },
    { accessorKey: 'unidadNumero', header: 'Unidad' },
    {
      accessorKey: 'tipo',
      header: 'Tipo',
      cell: ({ row }) => tipoBadge(row.original.tipo),
    },
    {
      accessorKey: 'ingresoAt',
      header: 'Ingreso',
      cell: ({ row }) => (
        <span className="text-[var(--color-ink-muted)]">
          {format(new Date(row.original.ingresoAt), 'd MMM, HH:mm', {
            locale: es,
          })}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="outline"
          disabled={isPending}
          onClick={() => registrarEgreso(row.original.id)}
        >
          Registrar egreso
        </Button>
      ),
    },
  ];

  const items = data?.items ?? [];

  return (
    <>
      <div className="mb-4 rounded-md bg-[var(--color-paper-dim)] px-4 py-3 text-sm">
        {items.length}{' '}
        {items.length === 1 ? 'visita' : 'visitas'} dentro del condominio ahora.
      </div>
      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        empty={
          <span className="text-sm text-[var(--color-ink-muted)]">
            No hay ingresos activos.
          </span>
        }
      />
    </>
  );
}
